/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import './app.css-import';
import { createApp } from 'vue';
import VueMatomo from 'vue-matomo';
import { IonicVue } from '@ionic/vue';
import Vue3Tour from 'vue3-tour';
import otuService from './services/onetimeupdate.service';
import bootstrapAppService from './services/bootstrapApp.service';
import sideEffects from './services/sideEffect.service';
import mountGlobalComponents from './services/mountGlobalComponent.service';
import { initializeSentry } from './services/error-handler/sentry/initializeSentry';
import { registerVueSentry } from './services/error-handler/sentry/registerVueSentry';
import sharedMethods from './mixins/sharedMethods.mixin';
import preBootstrapUpdateService from './services/preBootstrapUpdate.service';
import inAppPurchaseService from './services/inAppPurchase.service';
import capacitorService from './services/capacitor.service';
import appStateService from './services/appState.service';
import ConfigDeviceSettings from './config/device.config';
import ConfigApplicationSettings from './config/application.config';
import EventConst from './constants/event.constant';
import Emitter from './bus';
import { store } from './store/store';
import { initApplication } from './initApplication';
import router from './router';
import App from './App.vue';
import { presentNetworkToast } from './toast/presentNetworkToast';
import { initializePaypal } from './services/paypal/initializePaypal';
import { asyncAuthFlow } from './services/auth/asyncAuthFlow';
import { supabaseMigration } from './services/supabaseMigration/supabaseMigration';
import { setNetworkStatus } from './services/connectivity/setNetworkStatus';
import { startNetworkWatcher } from './services/connectivity/startNetworkWatcher';
import { getIsConnectedToInternet } from './services/connectivity/getIsConnectedToInternet';
import { MatomoConfigure } from './config/matomo.config';
import { doMagicLinkLogin } from './services/auth/doMagicLinkLogin';

capacitorService.setIsCapacitorMobileApp().then(() => {
  initializeSentry();

  let applicationBootstrapped = false;

  const app = createApp(App)
    .use(IonicVue)
    .use(router)
    .use(store)
    .use(Vue3Tour)
    .use(VueMatomo, MatomoConfigure(router))
    .mixin(sharedMethods);

  /**
   * Register vue error handler
   */
  registerVueSentry(app);

  function hidePreLoadContainer() {
    const ele = document.querySelector('.pre-load-container');
    ele.style.display = 'none';
  }
  const mountVueApp = async () => {
    mountGlobalComponents(app);
    await router.isReady();
    app.mount('#app');
    hidePreLoadContainer();
  };

  function startApplicationBootstrap() {
    applicationBootstrapped = true;
    capacitorService
      .configureStatusBar()
      .then(ConfigDeviceSettings.loadRequiredThings)
      .then(preBootstrapUpdateService.oneTimePreBootstrapUpdate)
      .then(otuService.doOneTimeUpdate)
      .then(ConfigDeviceSettings.loadDeviceSpecificConfig)
      .then(bootstrapAppService.loadRequiredDataSource)
      .then(ConfigApplicationSettings.loadApplicationConfig)
      .then(initializePaypal)
      .then(supabaseMigration)
      .then(asyncAuthFlow)
      .then(mountVueApp)
      .then(initApplication);
  }

  function main() {
    if (applicationBootstrapped === false) {
      doMagicLinkLogin().then(() => {
        capacitorService
          .getDeviceInfo()
          .then(appStateService.bindApplicationState)
          .then(startNetworkWatcher)
          .then(setNetworkStatus)
          .then(() => {
            if (getIsConnectedToInternet()) {
              startApplicationBootstrap();
              inAppPurchaseService.load();
            } else {
              presentNetworkToast();
            }
          });
      });
    }
  }

  preBootstrapUpdateService.isBootstrappingAllowed().then((allowed) => {
    if (allowed) {
      sideEffects().then(main);
    }
  });

  Emitter.on(EventConst.networkBackOnline, main);
});
